import { memo, useMemo } from 'react';
import * as THREE from 'three';
import { Line, Html } from '@react-three/drei';
import { useSketchStore } from '../state/useSketchStore';
import { useAppStore } from '../state/store';
import { useDocumentStore } from '../state/useDocumentStore';
import { resolveVisibility, currentVisibilityInput } from './visibility';
import { dispatchEntityClick, useEntityRegistryRef } from './toolEventBus';
import { buildTopology } from '../core/sketch/topology';
import { faceToGeometry } from '../core/sketch/triangulate';
import { solidEffectiveVisible, type SketchSolid } from '../core/sketch/types';
import { faceMaterialProps, viewModeToSketchStyle } from './sketch/faceMaterial';
import { BACKFACE_DEFINES } from './backfaceTint';

/**
 * Sketch Reference Layer (Phase B4 cross-visibility, the mirror image of
 * CadReferenceLayer / the BIM content layer).
 *
 * Mounts inside the shared viewports (2D/3D, quad previews, BIM 3D views)
 * and draws the document's sketch citizens — loose edges as line segments,
 * solids either as the SHADED face meshes (matching the Sketch workbench's
 * own face-per-mesh rendering, `shaded` mode) or as wireframes from their
 * shared-edge topology — so a face pushed/pulled or a line drawn at the
 * Sketch workbench is immediately visible and selectable everywhere else —
 * same Z-up world frame (A5), same ledger + VisibilityResolver language
 * (A4). Because sketch citizens live in the ledger (sceneSync mirror) and
 * edits route back through the edit router (B4), the reference is LIVE:
 * erase/gumball/property edits from this viewport write to the sketch store,
 * not to a copy.
 */
const REF_COLOR = '#9ecbff';
const REF_COLOR_SELECTED = '#00aaff';

export const SketchReferenceLayer = memo(function SketchReferenceLayer({ shaded = false }: { shaded?: boolean }) {
    const solids = useSketchStore((s) => s.solids);
    const edges = useSketchStore((s) => s.edges);
    const tags = useSketchStore((s) => s.tags);
    const dimensions = useSketchStore((s) => s.dimensions);
    const labels = useSketchStore((s) => s.labels);
    const selectedIds = useAppStore((s) => s.selectedIds);
    const ledger = useDocumentStore((s) => s.sceneObjects);

    // Visibility comes from the shared resolver (object/tag/layer/view
    // scope), never from sketch-store-local checks. Solids additionally obey
    // their own flag AND their tag's flag (same rule as the Sketch
    // workbench), so a hidden solid never ghosts back as a reference.
    const visibleIds = useMemo(() => {
        const input = currentVisibilityInput();
        const ids: string[] = [];
        for (const id of Object.keys(solids).concat(Object.keys(edges), Object.keys(dimensions), Object.keys(labels))) {
            const obj = ledger[id];
            if (!obj) continue;
            const r = resolveVisibility(obj, input);
            if (!r.visible) continue;
            const solid = solids[id];
            if (solid && !solidEffectiveVisible(solid, tags)) continue;
            ids.push(id);
        }
        return ids;
    }, [solids, edges, tags, dimensions, labels, ledger]);

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    return (
        <group>
            {visibleIds.map((id) => (
                <SketchReferenceItem key={id} itemId={id} isSelected={selectedSet.has(id)} shaded={shaded} />
            ))}
        </group>
    );
});

const SketchReferenceItem = memo(function SketchReferenceItem({
    itemId,
    isSelected,
    shaded = false,
}: {
    itemId: string;
    isSelected: boolean;
    shaded?: boolean;
}) {
    const solid = useSketchStore((s) => s.solids[itemId]);
    const edge = useSketchStore((s) => s.edges[itemId]);
    const dimension = useSketchStore((s) => s.dimensions[itemId]);
    const label = useSketchStore((s) => s.labels[itemId]);
    const viewMode = useAppStore((s) => s.view.viewMode);
    // Shaded mode mirrors the Sketch workbench's own display style, resolved
    // from the host viewport's shared view mode.
    const style = useMemo(() => faceMaterialProps(viewModeToSketchStyle(viewMode), undefined, solid?.materialId ?? ''), [viewMode, solid?.materialId]);
    // Wireframe fallback segments (loose edges / solid topology). Computed
    // unconditionally: hooks must not sit behind the shaded early return.
    const segments = useMemo(
        () => (edge ? edgeSegments(edge) : solidEdgeSegments(solid)),
        [solid, edge]
    );

    const registryRef = useEntityRegistryRef(itemId, 'sketch');

    // Annotations (dimensions / text labels) render the same way in every
    // host viewport — thin lines + DOM text, selected state highlighted —
    // and route clicks back into the shared selection space.
    if (dimension) {
        return <ReferenceDimension dimension={dimension} isSelected={isSelected} registryRef={registryRef} />;
    }
    if (label) {
        return <ReferenceLabel label={label} isSelected={isSelected} registryRef={registryRef} />;
    }

    if (solid && shaded) {
        return (
            <group ref={registryRef} onClick={(e) => { e.stopPropagation(); dispatchEntityClick(itemId, e as never); }}>
                <ShadedSolidFaces solid={solid} style={style} isSelected={isSelected} />
                {style.edgeOpacity > 0 && (
                    <ReferenceSolidEdges solid={solid} edgeColor={style.edgeColor} edgeOpacity={style.edgeOpacity} />
                )}
            </group>
        );
    }

    if (segments.length === 0) return null;

    const color = isSelected ? REF_COLOR_SELECTED : REF_COLOR;

    return (
        <group ref={registryRef} onClick={(e) => { e.stopPropagation(); dispatchEntityClick(itemId, e as never); }}>
            {segments.map((pts, i) => (
                <Line
                    key={i}
                    points={pts}
                    color={color}
                    lineWidth={isSelected ? 2 : 1}
                    transparent
                    opacity={isSelected ? 0.95 : 0.7}
                />
            ))}
        </group>
    );
});

/**
 * Shaded solid reference: one mesh per face, geometry derived from the same
 * analytic loops the Sketch workbench triangulates (core/sketch/triangulate)
 * and the same faceMaterial style language — so the reference looks exactly
 * like the solid does in the Sketch workbench under the equivalent style.
 */
function ShadedSolidFaces({ solid, style, isSelected }: {
    solid: SketchSolid;
    style: ReturnType<typeof faceMaterialProps>;
    isSelected: boolean;
}) {
    const faces = useMemo(
        () => solid.faces
            .map((face) => ({ id: face.id, geometry: faceToGeometry(face) }))
            .filter((face): face is { id: string; geometry: THREE.BufferGeometry } => face.geometry !== null),
        [solid]
    );
    if (faces.length === 0) return null;

    const color = isSelected ? REF_COLOR_SELECTED : style.color;
    const surfaceProps = {
        color,
        transparent: style.transparent || isSelected,
        opacity: isSelected ? 1 : style.opacity,
        depthWrite: style.depthWrite,
        side: THREE.DoubleSide,
        defines: style.backfaceTint ? BACKFACE_DEFINES : undefined,
    } as const;

    return (
        <group>
            {faces.map((face) => (
                <mesh key={face.id} geometry={face.geometry}>
                    {style.flat ? (
                        <meshBasicMaterial {...surfaceProps} />
                    ) : (
                        <meshStandardMaterial {...surfaceProps} roughness={style.roughness} metalness={style.metalness} />
                    )}
                </mesh>
            ))}
        </group>
    );
}

/** Edge overlay for a shaded solid reference (shared-edge graph segments). */
function ReferenceSolidEdges({ solid, edgeColor, edgeOpacity }: { solid: SketchSolid; edgeColor: string; edgeOpacity: number }) {
    const segments = useMemo(() => {
        const topo = buildTopology(solid);
        const pts: number[] = [];
        topo.edges.forEach((edge) => {
            pts.push(edge.a.x, edge.a.y, edge.a.z, edge.b.x, edge.b.y, edge.b.z);
        });
        return pts;
    }, [solid]);

    if (segments.length === 0) return null;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(segments), 3));

    return (
        <lineSegments geometry={geometry} raycast={() => null}>
            <lineBasicMaterial color={edgeColor} transparent={edgeOpacity < 1} opacity={edgeOpacity} />
        </lineSegments>
    );
}

type Pt3 = [number, number, number];

/** Loose edge → one line segment. */
function edgeSegments(edge: { a: [number, number, number]; b: [number, number, number] }): Pt3[][] {
    return [[[edge.a[0], edge.a[1], edge.a[2] ?? 0], [edge.b[0], edge.b[1], edge.b[2] ?? 0]]];
}

/** Solid → wireframe segments from the deduplicated shared-edge graph
 *  (same edges the Sketch workbench itself highlights). */
function solidEdgeSegments(solid: SketchSolid | undefined): Pt3[][] {
    if (!solid) return [];
    const topo = buildTopology(solid);
    const segments: Pt3[][] = [];
    topo.edges.forEach((edge) => {
        segments.push([
            [edge.a.x, edge.a.y, edge.a.z],
            [edge.b.x, edge.b.y, edge.b.z],
        ]);
    });
    return segments;
}

// ------------------------------------------------------- annotations (B4)

const ANNOTATION_TEXT_Z = [8, 0] as [number, number];

/** Shared annotation text chip (mirrors the Sketch workbench's label). */
function ReferenceAnnotationText({ position, text, color }: {
    position: THREE.Vector3;
    text: string;
    color: string;
}) {
    return (
        <Html position={position} center zIndexRange={ANNOTATION_TEXT_Z} style={{ pointerEvents: 'none' }}>
            <span
                style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color,
                    background: 'rgba(255,255,255,0.88)',
                    border: `1px solid ${color}44`,
                    borderRadius: 4,
                    padding: '1px 5px',
                    whiteSpace: 'nowrap',
                    fontFamily: 'inherit',
                }}
            >
                {text}
            </span>
        </Html>
    );
}

/** Invisible pick dot: lines alone are too thin to click reliably. */
function ReferencePickDot({ point }: { point: THREE.Vector3 }) {
    return (
        <mesh position={point}>
            <sphereGeometry args={[0.12, 8, 8]} />
            <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
    );
}

/** Sketch dimension reference: witness lines + dim line + value text,
 *  drawn with the same geometry the Sketch workbench's own annotation
 *  layer uses, in the host viewport's selection colors. */
function ReferenceDimension({ dimension, isSelected, registryRef }: {
    dimension: { id: string; a: [number, number, number]; b: [number, number, number]; offset: [number, number, number]; value: number; text?: string };
    isSelected: boolean;
    registryRef: (obj: THREE.Object3D | null) => void;
}) {
    const color = isSelected ? REF_COLOR_SELECTED : REF_COLOR;
    const lines = useMemo(() => {
        const a = new THREE.Vector3(...dimension.a);
        const b = new THREE.Vector3(...dimension.b);
        const offset = new THREE.Vector3(...dimension.offset);
        const dir = b.clone().sub(a);
        if (dir.lengthSq() < 1e-12) return [];
        dir.normalize();
        const footA = a.clone().addScaledVector(dir, offset.clone().sub(a).dot(dir));
        const footB = b.clone().addScaledVector(dir, offset.clone().sub(b).dot(dir));
        return [
            [a, footA],
            [b, footB],
            [footA, footB],
        ];
    }, [dimension.a, dimension.b, dimension.offset]);
    if (lines.length === 0) return null;

    return (
        <group
            ref={registryRef}
            onClick={(e) => { e.stopPropagation(); dispatchEntityClick(dimension.id, e as never); }}
        >
            {lines.map(([p, q], i) => (
                <Line
                    key={i}
                    points={[p.toArray(), q.toArray()]}
                    color={color}
                    lineWidth={isSelected ? 2 : 1}
                    dashed={i < 2}
                    dashSize={0.18}
                    gapSize={0.12}
                    transparent
                    opacity={isSelected ? 0.95 : 0.7}
                />
            ))}
            <ReferencePickDot point={new THREE.Vector3(...dimension.offset)} />
            <ReferenceAnnotationText
                position={new THREE.Vector3(...dimension.offset)}
                text={dimension.text ?? dimension.value.toFixed(2)}
                color={color}
            />
        </group>
    );
}

/** Sketch text label reference: leader line + anchored text. */
function ReferenceLabel({ label, isSelected, registryRef }: {
    label: { id: string; anchor: [number, number, number]; leader: [number, number, number]; text: string };
    isSelected: boolean;
    registryRef: (obj: THREE.Object3D | null) => void;
}) {
    const color = isSelected ? REF_COLOR_SELECTED : REF_COLOR;
    return (
        <group
            ref={registryRef}
            onClick={(e) => { e.stopPropagation(); dispatchEntityClick(label.id, e as never); }}
        >
            <Line
                points={[label.anchor, label.leader]}
                color={color}
                lineWidth={isSelected ? 2 : 1}
                transparent
                opacity={isSelected ? 0.95 : 0.7}
            />
            <ReferencePickDot point={new THREE.Vector3(...label.anchor)} />
            <ReferenceAnnotationText
                position={new THREE.Vector3(...label.leader)}
                text={label.text}
                color={color}
            />
        </group>
    );
}
